import React from "react";
import { Link } from "react-router-dom";
import { Lock, CreditCard, CheckCircle2, MessageSquare, Clock3, Sparkles, ArrowRight } from "lucide-react";
import { PROVIDER_LABEL, IS_QUICKBOOKS } from "@/lib/payments";
import { BRAND } from "@/lib/brand";

/**
 * A reassurance block designed to sit alongside the booking form or the custom
 * quote request form. Explains the deposit, remaining balance, and what happens
 * step-by-step after submission.
 *
 * Props:
 *   variant: "booking" (default) — for /quote (deposit flow)
 *            "custom-quote"      — for /request-quote (no-deposit flow)
 */
export default function WhatToExpect({ variant = "booking" }) {
  const isBooking = variant === "booking";

  const bookingSteps = [
    {
      icon: Sparkles,
      title: "Pick your date, service, and add-ons",
      body: "Use the live calendar to pick an open time slot. Every price you see is final — no hidden fees.",
    },
    {
      icon: CreditCard,
      title: `$50 deposit via secure ${PROVIDER_LABEL} checkout`,
      body: `Your $50 deposit locks in your appointment and applies toward the total. It's processed through ${IS_QUICKBOOKS ? "QuickBooks Payments" : "Stripe"} — we never see or store your card number.`,
    },
    {
      icon: MessageSquare,
      title: "We confirm within 24 hours",
      body: "You'll get a confirmation email/text with your booking details and our team's arrival window.",
    },
    {
      icon: CheckCircle2,
      title: "Remaining balance charged after service",
      body: "You only pay the balance once the work is done and you're happy with the result. We send you a secure payment link — no surprise charges, no card-on-file authorizations.",
    },
  ];

  const quoteSteps = [
    {
      icon: MessageSquare,
      title: "Tell us about your project",
      body: "Fill out the form with a short description and (optionally) photos. Takes about 2 minutes.",
    },
    {
      icon: Clock3,
      title: "We reply within 1 business day",
      body: "A real person reviews your request and gets back to you by phone, text, or email — whichever you prefer.",
    },
    {
      icon: CreditCard,
      title: "Zero cost to get a quote",
      body: "There's no charge, no deposit, and no obligation to book until we've agreed on scope and price together.",
    },
    {
      icon: CheckCircle2,
      title: "Payment only after the job is complete",
      body: "Once we've scoped the work and picked a date, you pay only after the project is finished to your satisfaction. Invoicing is sent through a secure payment link.",
    },
  ];

  const steps = isBooking ? bookingSteps : quoteSteps;
  const title = isBooking ? "What Happens Next" : "How Custom Quotes Work";
  const subtitle = isBooking
    ? "A quick walkthrough of what your $50 deposit covers and what happens after you book."
    : "A short, straightforward look at how we handle project requests — no pressure, no hidden fees.";

  return (
    <aside
      data-testid={`what-to-expect-${variant}`}
      className="rounded-2xl bg-gradient-to-br from-[var(--brand-cream-soft)] to-white border-2 border-[#e6e0d4] p-6 md:p-7"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-[var(--brand-teal)] text-white flex items-center justify-center flex-shrink-0">
          <CheckCircle2 className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h3 className="font-extrabold text-[var(--brand-teal)] text-lg leading-tight" style={{ fontFamily: "Outfit" }}>
            {title}
          </h3>
          <p className="text-xs text-slate-500 mt-1 leading-relaxed">{subtitle}</p>
        </div>
      </div>

      <ol className="space-y-4 mt-2">
        {steps.map((s, idx) => (
          <li key={s.title} className="flex items-start gap-3">
            <div className="w-7 h-7 rounded-full bg-[var(--brand-gold)]/20 text-[var(--brand-gold-deep)] font-extrabold text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
              {idx + 1}
            </div>
            <div>
              <div className="flex items-center gap-2 font-bold text-sm text-[var(--brand-teal)]" style={{ fontFamily: "Outfit" }}>
                <s.icon className="w-4 h-4 text-[var(--brand-gold)]" />
                {s.title}
              </div>
              <p className="text-xs text-slate-600 mt-1 leading-relaxed">{s.body}</p>
            </div>
          </li>
        ))}
      </ol>

      <div className="mt-5 pt-4 border-t border-[#e6e0d4] text-xs text-slate-500 leading-relaxed flex items-start gap-2">
        <Lock className="w-3.5 h-3.5 text-[var(--brand-gold)] flex-shrink-0 mt-0.5" />
        <span>
          Questions before you {isBooking ? "book" : "submit"}? Call us anytime at{" "}
          <a href={BRAND.phoneHref} className="text-[var(--brand-teal)] font-semibold underline">{BRAND.phone}</a>
          {isBooking ? (
            <> — or if you're outside our 20-mile cleaning radius, <Link to="/request-quote" className="text-[var(--brand-teal)] font-semibold underline">request a custom quote</Link> instead.</>
          ) : (
            <> — or if you need a cleaning within 20 miles of Evansville, <Link to="/quote" className="text-[var(--brand-teal)] font-semibold underline">book online instantly</Link>.</>
          )}
        </span>
      </div>
    </aside>
  );
}
